/**
 * Exports Base44 Product records to a CSV file with column headings
 * aligned 1:1 to Square's catalog CSV format.
 *
 * Square-native columns use the exact Square heading name.
 * Base44-only columns are prefixed "Base44:" so they are visually distinct
 * and can be filtered out when diffing against a Square export.
 *
 * EXCLUSIONS (conscious, with reason):
 *   "New Quantity"      — Square write-only column for stock adjustments; not stored in Base44.
 *   "Handle"            — Square Online internal ID; not meaningful in Base44.
 *   "Google Product Category" — not used by this store.
 */

// [CSV heading (Square name where applicable), Base44 field key]
const COLUMNS = [
  // ── IDENTITY ──────────────────────────────────────────────────────
  ['Token',                           'square_item_id'],
  ['Variation Token',                 'square_variation_id'],
  ['Parent Item Token',               'square_parent_item_id'],

  // ── ITEM-LEVEL ────────────────────────────────────────────────────
  ['Item Name',                       'name'],
  ['Customer-Facing Name',            'customer_facing_name'],
  ['Description',                     'description'],
  ['Reporting Category',              'reporting_category'],

  // ── VARIATION-LEVEL ───────────────────────────────────────────────
  ['Variation Name',                  'variation_name'],
  ['SKU',                             'sku'],
  ['GTIN',                            'gtin'],
  ['Price',                           'price'],
  ['Option Name 1',                   'option_name'],
  ['Option Value 1',                  'option_value'],

  // ── INVENTORY ─────────────────────────────────────────────────────
  ['Current Quantity',                'stock_quantity'],
  ['Stockable',                       'stockable'],
  ['Sellable',                        'sellable'],
  ['In Stock',                        'in_stock'],

  // ── FULFILMENT FLAGS ──────────────────────────────────────────────
  ['Shipping Enabled',                'shipping_enabled'],
  ['Delivery Enabled',                'delivery_enabled'],
  ['Pickup Enabled',                  'pickup_enabled'],

  // ── WEIGHT ────────────────────────────────────────────────────────
  ['Weight',                          'weight'],
  ['Weight Unit',                     'weight_unit'],

  // ── SQUARE ONLINE ─────────────────────────────────────────────────
  ['Square Online Item Visibility',   'square_online_visibility'],
  ['SEO Title',                       'seo_title'],
  ['SEO Description',                 'seo_description'],
  ['Permalink',                       'permalink'],

  // ── IMAGES ────────────────────────────────────────────────────────
  ['Image URL 1',                     'image_url'],
  ['Image URL 2',                     'image_url_2'],
  ['Image URL 3',                     'image_url_3'],
  ['Image URL 4',                     'image_url_4'],

  // ── VISIBILITY / STATUS ───────────────────────────────────────────
  ['Published',                       'published'],

  // ── BASE44-ONLY FIELDS ────────────────────────────────────────────
  ['Base44: ID',                      'id'],
  ['Base44: Category Enum',           'category'],
  ['Base44: Platform',                'platform'],
  ['Base44: Is Variant',              'is_variant'],
  ['Base44: Featured',                'featured'],
  ['Base44: Specs',                   'specs'],
  ['Base44: Created Date',            'created_date'],
  ['Base44: Updated Date',            'updated_date'],
  ['Base44: Created By',              'created_by'],
];

function escapeCsv(value) {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return '"' + str.replace(/"/g, '""') + '"';
  }
  return str;
}

export function exportProductsToCsv(products) {
  const headers = COLUMNS.map(([heading]) => escapeCsv(heading)).join(',');
  const rows = products.map(p =>
    COLUMNS.map(([, key]) => escapeCsv(p[key])).join(',')
  );

  const csv = [headers, ...rows].join('\r\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' }); // BOM for Excel UTF-8
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `products_export_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}